import React, { useState, useRef, useEffect } from 'react';
import copy from 'copy-to-clipboard';
import { useSlateStatic, useReadOnly, useSelected } from 'slate-react';
import { isFuture, parseISO } from 'date-fns';
import { icons, Button } from '@ublend-npm/aulaui-next';
import { LtiProvider } from '@ublend-npm/aula-schema';

import { Loading } from '../Loading/Loading';
import { AssignmentEntity } from '../../Entities';
import { useAulaEditorContext } from '../../EditorContext';
import {
  AssignmentBlock,
  LoaderContainer,
  ButtonContainer,
  Form,
  IntegrationDisabledAlert,
} from './Assignment.styled';
import { removeVoidElement } from '../../lib/commands';
import { BlockToolbar } from '../BlockToolbar/BlockToolbar';
import { useFetchData } from './useFetchData';
import {
  AssignmentDetails,
  InvalidAssignmentDetails,
  Lti1p3AssignmentDetails,
  Provider,
} from './assignmentType';
import { AssignmentProperties } from './AssignmentProperties';
import { InvalidAssignment } from './InvalidAssignment';
import { AssignmentTitle } from './AssignmentTitle';
import { StatusTypes } from '../../lib/loadingStatusTypes';
import { fetchTurnitinLaunchRequest } from './utils/fetchTurnitinLaunchRequest';
import { BlockElement } from '../../types';
import { lti1p3Start } from './utils/lti1p3Start';
import { LtiRequestType } from './utils/types';
import { useAnalytics } from '../../lib/useAnalytics';

export type AssignmentProps = {
  assignmentsEndpoint: string;
  element: BlockElement;
};

const TurnitinLTIEndpoint = 'lti-consumer';

const getAssignmentProvider = (
  ltiProviders: LtiProvider[] | undefined = [],
  assignmentDetails: AssignmentDetails | Lti1p3AssignmentDetails,
) => {
  if (assignmentDetails.provider === Provider.LTI1p3) {
    return ltiProviders.find(
      (provider) => provider.id === assignmentDetails.providerId,
    );
  }

  return ltiProviders.find(
    (provider) => provider.name === assignmentDetails.provider,
  );
};

export const Assignment = ({
  assignmentsEndpoint,
  element,
}: AssignmentProps) => {
  const [isHovering, setHovering] = useState(false);
  const [ltiRequest, setLtiRequest] = useState<{
    url: string;
    method: string;
    body: {
      [key: string]: string;
    };
  } | null>(null);

  const editor = useSlateStatic();
  const isReadOnly = useReadOnly();
  const isSelected = useSelected();
  const formRef = useRef<HTMLFormElement>(null);
  const track = useAnalytics();

  const {
    entities,
    config: { institution, spaceId, userId, apiUrl, ltiProviders },
  } = useAulaEditorContext();
  const { entity: entityId } = element;
  const selectedEntity = entities[entityId] as AssignmentEntity | undefined;
  const deleteBlock = () => removeVoidElement(editor, element);

  const { type, assignmentId, savedAssignmentDetails } = selectedEntity || {};

  const assignmentsUrl =
    assignmentId && assignmentsEndpoint
      ? `${assignmentsEndpoint}/${assignmentId}/details`
      : undefined;
  const ltiConsumerEndpoint = `${apiUrl}/${TurnitinLTIEndpoint}`;

  const { state } = useFetchData(assignmentsUrl);
  const { data: fetchedAssignmentDetails, status: fetchedStatus } = state;

  const assignmentDetails: AssignmentDetails =
    savedAssignmentDetails?.assignmentDetails ||
    fetchedAssignmentDetails?.assignmentDetails;

  const isEducator: boolean =
    savedAssignmentDetails?.isEducator || fetchedAssignmentDetails?.isEducator;

  const status = savedAssignmentDetails ? StatusTypes.SUCCESS : fetchedStatus;
  const isLoading = status === StatusTypes.LOADING;
  const hasError = status === StatusTypes.FAILED;

  const handinAssignmentLink = (details: AssignmentDetails) =>
    `https://handin.${institution}.aula.education/${spaceId}/${details.externalId}`;

  const ssaAssignmentLink = (details: AssignmentDetails) =>
    `https://success.${institution}.aula.education/${spaceId}/assignment/${details.id}`;

  const quizzesAssignmentLink = (details: AssignmentDetails) =>
    isEducator
      ? `https://${institution}.aula.education/#/dashboard/${spaceId}/journey/assignments/${details.id}/submissions`
      : `https://${institution}.aula.education/#/dashboard/${spaceId}/journey/quizzes/${details.id}`;

  const openTurnitin = async (details: AssignmentDetails) => {
    const formData = await fetchTurnitinLaunchRequest({
      assignmentDetails: details,
      spaceId,
      userId,
      ltiConsumerEndpoint,
    });
    if (formData?.body && formData?.url && formData?.method) {
      setLtiRequest(formData);
    }
  };

  const openLink = (link: string) => {
    window.open(link);
  };

  const openLti1p3 = async (details: AssignmentDetails) => {
    if (!ltiProviders) {
      console.error('Can not launch LTI 1p3 without context on ltiProviders');
      return;
    }

    if (!details.providerId) {
      console.error('Can not launch LTI 1p3 without providerId');
      return;
    }

    if (!details.resourceLinkId) {
      console.error('Can not launch LTI 1p3 without resourceLinkId');
      return;
    }

    const provider = ltiProviders.find(
      (prov) => prov.id === details.providerId,
    );

    if (!provider) {
      console.error('Can not find this LTI provider in context');
      return;
    }

    if (provider.ltiVersion !== '1.3') {
      console.error('Can only launch LTI 1.3 integrations');
      return;
    }

    const { clientId, iss, loginUrl, targetUrl, description } = provider;

    let ltiSessionToken;

    try {
      track('LTI_ASSIGNMENT_LAUNCH', {
        itemType: 'assignment',
        assignmentId: details.id,
        provider: description,
      });
      ltiSessionToken = await lti1p3Start({
        userId,
        spaceId,
        apiUrl,
        type: LtiRequestType.RESOURCE_LINK,
        clientId,
        assignmentId: details.id,
        resourceLinkId: details.resourceLinkId,
      });
    } catch (err) {
      console.error(err);
    }

    if (!ltiSessionToken) {
      return;
    }

    setLtiRequest({
      method: 'post',
      url: loginUrl,
      body: {
        iss,
        clientId,
        target_link_uri: targetUrl,
        login_hint: ltiSessionToken,
      },
    });
  };

  const openAssignment = (details: AssignmentDetails) => {
    switch (details.provider) {
      case Provider.Handin:
        return openLink(handinAssignmentLink(details));
      case Provider.TurnitIn:
        return openTurnitin(details);
      case Provider.Manual:
        return openLink(ssaAssignmentLink(details));
      case Provider.Quizzes:
        return openLink(quizzesAssignmentLink(details));
      case Provider.LTI1p3:
        return openLti1p3(details);
      default:
        return () => {};
    }
  };

  const invalidAssignmentDetails =
    assignmentDetails as InvalidAssignmentDetails;
  const isInvalidAssignment =
    !invalidAssignmentDetails ||
    invalidAssignmentDetails?.deletedAt ||
    invalidAssignmentDetails?.hiddenFromStudent;

  const assignmentHasNotStartedYet = () =>
    assignmentDetails?.startDate &&
    isFuture(parseISO(assignmentDetails?.startDate));

  const isQuiz = (details: AssignmentDetails) =>
    details.provider === Provider.Quizzes;

  const isQuizDisabled = (details: AssignmentDetails) =>
    details.submission?.submittedAt ||
    details.status === 'closed' ||
    assignmentHasNotStartedYet();

  const disableQuizAccess = (details: AssignmentDetails) =>
    isQuiz(details) && isQuizDisabled(details) && !isEducator;

  const assignmentProvider = !isInvalidAssignment
    ? getAssignmentProvider(ltiProviders, assignmentDetails)
    : undefined;
  const assignmentProviderDisabled = !!assignmentProvider?.disabled;

  /**
   * Disable assignment opening if:
   * - Assignment details are still being fetched
   * - Invalid assignment (deleted, hidden from student)
   * - startDate exists and Assignment is in the future and user is not an educator
   * - Assignment provider is manual and the user is not an educator
   * - Assignment provider is quizzes, has a submission or is closed and the user is not an educator
   * - Assignment provider has been disabled
   */
  const disableAssignmentOpening =
    isLoading ||
    !!isInvalidAssignment ||
    (!isEducator && assignmentHasNotStartedYet()) ||
    (assignmentDetails?.provider === Provider.Manual && !isEducator) ||
    !!disableQuizAccess(assignmentDetails) ||
    assignmentProviderDisabled;

  useEffect(() => {
    if (ltiRequest) {
      formRef.current?.submit();
    }
  }, [ltiRequest]);

  const toolbarItems = (details: AssignmentDetails) => {
    if (!details) {
      return [];
    }

    const copyLink = () => {
      const isHandin = details.provider === Provider.Handin;
      const isQuizzes = details.provider === Provider.Quizzes;
      if (isHandin || (isQuizzes && !disableQuizAccess(details))) {
        const link = isHandin
          ? handinAssignmentLink(details)
          : quizzesAssignmentLink(details);

        return [
          {
            title: 'Copy link',
            icon: icons.CopyLink,
            onAction: () => copy(link),
          },
        ];
      }

      return [];
    };

    const openAssignmentLink = () =>
      !disableAssignmentOpening
        ? [
            {
              title: 'Open in new tab',
              icon: icons.OpenInNew,
              onAction: () => openAssignment(details),
            },
          ]
        : [];

    return [
      ...copyLink(),
      {
        title: 'Delete',
        icon: icons.Delete,
        onAction: () => removeVoidElement(editor, element),
        isVisible: !isReadOnly,
      },
      ...openAssignmentLink(),
    ];
  };

  if (!selectedEntity) {
    return (
      <AssignmentBlock
        data-testid="assignment"
        isSelected={isSelected}
        isDisabled
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        target="_blank"
        rel="noreferrer"
        tabIndex={-1}
        onClick={(e) => {
          e.preventDefault();
        }}
      >
        <InvalidAssignment />
      </AssignmentBlock>
    );
  }

  if (type !== 'assignment') {
    throw new Error(
      'not allowed to render AssignmentBlock without assignment entity',
    );
  }

  const renderAssignmentDetails = (details: AssignmentDetails) => (
    <>
      {assignmentProviderDisabled ? <IntegrationDisabledAlert /> : null}
      <AssignmentProperties
        assignmentDetails={details as AssignmentDetails}
        isEducator={isEducator}
      />
      <AssignmentTitle assignmentDetails={details as AssignmentDetails} />
      <ButtonContainer>
        {!disableAssignmentOpening && (
          <Button
            type="text"
            size="small"
            data-testid="go-to-assignment"
            iconRight={() => (
              <icons.ArrowForward
                fontSize="small"
                data-testid="arrow-forward"
              />
            )}
            onClick={(e: React.SyntheticEvent) => {
              e.stopPropagation();
              void openAssignment(details);
            }}
          >
            Go to assignment
          </Button>
        )}
      </ButtonContainer>
      <BlockToolbar
        isVisible={isSelected || isHovering}
        items={toolbarItems(details)}
      />
      {ltiRequest && (
        <Form
          data-testid="assignment-block-lti-form"
          method={ltiRequest.method}
          action={ltiRequest.url}
          ref={formRef}
          target="_blank"
        >
          {Object.entries(ltiRequest.body).map(([key, value]) => (
            <input key={key} name={key} value={value} readOnly />
          ))}
        </Form>
      )}
    </>
  );

  return (
    <AssignmentBlock
      data-testid="assignment"
      isSelected={isSelected}
      isDisabled={disableAssignmentOpening}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      target="_blank"
      rel="noreferrer"
      tabIndex={-1}
      onClick={(e) => {
        e.preventDefault();
        if (!disableAssignmentOpening) {
          void openAssignment(assignmentDetails as AssignmentDetails);
        }
      }}
    >
      {isLoading ? (
        <LoaderContainer>
          <Loading />
        </LoaderContainer>
      ) : isInvalidAssignment || hasError ? (
        <InvalidAssignment
          assignment={invalidAssignmentDetails}
          deleteBlock={isEducator && !isReadOnly && deleteBlock}
        />
      ) : (
        renderAssignmentDetails(assignmentDetails as AssignmentDetails)
      )}
    </AssignmentBlock>
  );
};
